import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Activity,
  CheckCircle2,
  HeartPulse,
  HardDrive,
  LayoutDashboard,
  Timer,
  Boxes,
  X,
} from 'lucide-react';
import { PROVIDERS } from '@/config/providers';
import { cn } from '@/lib/utils';
import logoUrl from '@/assets/revocruit-logo.png';

const baseLinks = [
  { to: '/', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/hangfire', label: 'Hangfire', icon: Activity },
  { to: '/jobs', label: 'Cron jobs', icon: Timer },
  { to: '/storage', label: 'S3 storage', icon: HardDrive },
  { to: '/manifest', label: 'Harvest health', icon: HeartPulse },
  { to: '/completeness', label: 'Completeness', icon: CheckCircle2 },
];

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
    isActive
      ? 'bg-primary/10 text-primary'
      : 'text-foreground/80 hover:bg-muted hover:text-foreground',
  );

/**
 * Nav body shared by the desktop rail and the mobile drawer. `onNavigate`
 * fires after a link is chosen so the mobile drawer can close itself.
 */
function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <>
      <nav className="flex-1 space-y-6 overflow-y-auto px-2 py-4 scrollbar-thin">
        <div>
          <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Platform
          </div>
          {baseLinks.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} onClick={onNavigate} className={navLinkClass}>
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </div>

        <div>
          <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Providers
          </div>
          {PROVIDERS.map((p) => (
            <NavLink
              key={p.key}
              to={`/entities/${p.key}`}
              onClick={onNavigate}
              className={navLinkClass}
            >
              <Boxes className="h-4 w-4" />
              {p.label}
            </NavLink>
          ))}
        </div>
      </nav>

      <div className="border-t border-border p-3 text-[10px] text-muted-foreground">
        v{__APP_VERSION__} · designed to scale to N providers
      </div>
    </>
  );
}

function SidebarBrand() {
  return (
    <div className="flex h-14 items-center gap-2 border-b border-border px-4">
      <img
        src={logoUrl}
        alt="RevoCruit"
        className="h-6 w-6 shrink-0 rounded-sm object-contain"
      />
      <div className="text-sm font-semibold tracking-tight">RevoData Admin</div>
    </div>
  );
}

/** Desktop rail — hidden below the `lg` breakpoint. */
export function Sidebar() {
  return (
    <aside className="hidden h-full w-60 shrink-0 border-r border-border bg-card/40 lg:flex lg:flex-col">
      <SidebarBrand />
      <SidebarContent />
    </aside>
  );
}

/**
 * Mobile drawer — the only way to reach the nav below `lg`, where the desktop
 * rail is hidden. Renders an off-canvas panel + backdrop; closes on link
 * choice, backdrop tap, or Escape.
 */
export function MobileSidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
      <button
        type="button"
        aria-label="Close navigation"
        className="absolute inset-0 bg-background/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="absolute left-0 top-0 flex h-full w-64 flex-col border-r border-border bg-card shadow-xl">
        <div className="flex h-14 items-center justify-between border-b border-border px-4">
          <div className="flex items-center gap-2">
            <img
              src={logoUrl}
              alt="RevoCruit"
              className="h-6 w-6 shrink-0 rounded-sm object-contain"
            />
            <div className="text-sm font-semibold tracking-tight">RevoData Admin</div>
          </div>
          <button
            type="button"
            aria-label="Close navigation"
            className="rounded-md p-1.5 text-foreground/70 hover:bg-muted hover:text-foreground"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <SidebarContent onNavigate={onClose} />
      </div>
    </div>
  );
}
